import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";

import {
  createModule,
  deleteModule,
  getLicences,
  getModuleLicenceIds,
  getModules,
  syncModuleLicences,
  updateModule,
} from "./api";
import { ALL_APP_MODULE_NAMES } from "../../constants/appModules";

export default function ModulesTab() {
  const [modules, setModules] = useState([]);
  const [licences, setLicences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingModule, setEditingModule] = useState(null);
  const [moduleName, setModuleName] = useState("");
  const [selectedLicenceIds, setSelectedLicenceIds] = useState([]);
  const [saving, setSaving] = useState(false);

  const licenceNameById = licences.reduce((acc, licence) => {
    acc[String(licence.id)] = licence.name;
    return acc;
  }, {});

  const loadData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [modulesData, licencesData] = await Promise.all([
        getModules(),
        getLicences(),
      ]);
      setModules(Array.isArray(modulesData) ? modulesData : []);
      setLicences(Array.isArray(licencesData) ? licencesData : []);
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to load modules.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const openCreateDialog = () => {
    setEditingModule(null);
    setModuleName("");
    setSelectedLicenceIds([]);
    setDialogOpen(true);
  };

  const openEditDialog = (module) => {
    setEditingModule(module);
    setModuleName(module.name);
    setSelectedLicenceIds(getModuleLicenceIds(module));
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingModule(null);
    setModuleName("");
    setSelectedLicenceIds([]);
  };

  const handleSave = async () => {
    if (!moduleName.trim()) {
      setError("Module name is required.");
      return;
    }

    setSaving(true);
    setError("");
    try {
      if (editingModule) {
        await updateModule(editingModule.id, moduleName.trim());
        await syncModuleLicences(
          editingModule.id,
          selectedLicenceIds,
          licences,
        );
      } else {
        await createModule(moduleName.trim(), selectedLicenceIds);
      }
      closeDialog();
      await loadData();
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to save module.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (module) => {
    if (!window.confirm(`Delete module "${module.name}"?`)) return;

    setError("");
    try {
      await deleteModule(module.id);
      await loadData();
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to delete module.");
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={6}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Typography variant="h6">Modules</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateDialog}>
          Add Module
        </Button>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      <Alert severity="info" sx={{ mb: 2 }}>
        <strong>EPCM</strong> and <strong>Estimation</strong> are parent modules. Add
        sub-modules separately (EMS, PCS, Estimation Piping, Estimation PV, …).
        Names: {ALL_APP_MODULE_NAMES.join(", ")}
      </Alert>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: "#f3f1fe" }}>
              <TableCell>Module</TableCell>
              <TableCell>Linked Licences</TableCell>
              <TableCell align="right" width={120}>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {modules.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} align="center">
                  No modules yet.
                </TableCell>
              </TableRow>
            ) : (
              modules.map((module) => {
                const moduleLicenceIds = getModuleLicenceIds(module);

                return (
                  <TableRow key={module.id} hover>
                    <TableCell>{module.name}</TableCell>
                    <TableCell>
                      <Stack direction="row" flexWrap="wrap" gap={0.5}>
                        {moduleLicenceIds.length ? (
                          moduleLicenceIds.map((licenceId) => (
                            <Chip
                              key={`${module.id}-${licenceId}`}
                              label={licenceNameById[licenceId] || licenceId}
                              size="small"
                              variant="outlined"
                            />
                          ))
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            Not linked to any licence
                          </Typography>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => openEditDialog(module)}
                        aria-label="Edit module"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDelete(module)}
                        aria-label="Delete module"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingModule ? "Edit Module" : "Add Module"}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Module Name"
              value={moduleName}
              onChange={(e) => setModuleName(e.target.value)}
              fullWidth
              autoFocus
            />

            {licences.length === 0 ? (
              <Alert severity="info">
                Create licences on the Licences & Users tab first.
              </Alert>
            ) : (
              <FormControl fullWidth>
                <InputLabel>Licences</InputLabel>
                <Select
                  multiple
                  label="Licences"
                  value={selectedLicenceIds}
                  onChange={(e) => {
                    const value = e.target.value;
                    setSelectedLicenceIds(
                      typeof value === "string" ? value.split(",") : value,
                    );
                  }}
                  input={<OutlinedInput label="Licences" />}
                  renderValue={(selected) =>
                    selected
                      .map((id) => licenceNameById[id] || id)
                      .join(", ") || "None"
                  }
                >
                  {licences.map((licence) => (
                    <MenuItem key={licence.id} value={String(licence.id)}>
                      <Checkbox
                        checked={selectedLicenceIds.includes(String(licence.id))}
                      />
                      <ListItemText primary={licence.name} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <Typography variant="body2" color="text.secondary">
              {editingModule
                ? "Licence links are updated via each licence's module list when you save."
                : "Optionally link this module to one or more licences on create."}
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
